import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaEye, FaArrowRight } from 'react-icons/fa';

import { SPECIALTY_PLAN_CATALOG, isOrganicOnlySpecialtyPlan } from '../../data/specialtyMealPlans';
import { MEAL_SLOT_TYPES } from '../../data/mealSlotTypes';
import {
  SPECIALTY_UI_OVERLAYS,
  buildPlanFromApi,
  pickFromPriceRow,
} from '../../data/specialtyCatalogApi';
import { resolveSpecialtyIcon } from '../../data/specialtyIcons';
import {
  USE_API_MEAL_PLANS,
  fetchMealPlanCatalog,
  fetchIndicativeMenu,
} from '../../api/mealPlansApi';
import SpecialtyPlanDetailModal from './SpecialtyPlanDetailModal';
import SelectPlanModal from './SelectPlanModal';
import './SpecialtyPlans.css';

function overlayFor(specialtyOrCode) {
  const code = typeof specialtyOrCode === 'string' ? specialtyOrCode : specialtyOrCode?.code;
  const specialty = typeof specialtyOrCode === 'object' ? specialtyOrCode : null;
  const base = SPECIALTY_UI_OVERLAYS[code] || {};
  const iconCode = specialty?.iconCode || base.iconCode || code;
  return {
    ...base,
    tagline: specialty?.caption || base.tagline || '',
    iconCode,
    logoUrl: specialty?.logoUrl || base.logoUrl || null,
    iconComponent: resolveSpecialtyIcon(iconCode, code),
  };
}

export default function SpecialtyPlansSection({
  onSelectPlan,
  selectedPlanId,
  hideSelectButton = false,
}) {
  const [detailPlan, setDetailPlan] = useState(null);
  const [selectPlan, setSelectPlan] = useState(null);
  const [apiSlots, setApiSlots] = useState([]);
  const [apiPrices, setApiPrices] = useState([]);
  const [apiSpecialties, setApiSpecialties] = useState([]);
  const [plans, setPlans] = useState(SPECIALTY_PLAN_CATALOG);
  const [catalogSource, setCatalogSource] = useState('local');
  const [catalogError, setCatalogError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!USE_API_MEAL_PLANS) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const { specialties, slots, prices } = await fetchMealPlanCatalog();
        if (cancelled) return;
        const nextPlans = (specialties || [])
          .filter((s) => s.status !== 'hidden')
          .slice()
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((specialty) =>
            buildPlanFromApi({
              specialty,
              slots,
              prices,
              indicativeItems: [],
              overlay: overlayFor(specialty),
            }),
          )
          .filter(Boolean);

        if (nextPlans.length) {
          setApiSpecialties(specialties);
          setApiSlots(slots);
          setApiPrices(prices);
          setPlans(nextPlans);
          setCatalogSource('api');
          setCatalogError('');
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('Meal plan specialty catalog API failed; using local catalog.', err);
        // Keep default cards visible — do not surface raw network noise as a blocking error.
        setCatalogError('Live specialty catalog is temporarily unavailable. Showing default plans.');
        setPlans(SPECIALTY_PLAN_CATALOG);
        setCatalogSource('local');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const openDetails = useCallback(
    async (plan) => {
      if (!plan) return;
      if (catalogSource !== 'api' || !USE_API_MEAL_PLANS) {
        setDetailPlan(plan);
        return;
      }

      setDetailLoading(true);
      setDetailPlan({ ...plan, weeklyMenu: plan.weeklyMenu || [], _loadingMenu: true });
      try {
        const items = await fetchIndicativeMenu({ specialtyCode: plan.id });
        const specialty =
          apiSpecialties.find((s) => s.code === plan.id)
          || {
            id: plan.specialtyId,
            code: plan.id,
            name: plan.name,
            supportsRegion: plan.supportsRegion,
            caption: plan.caption || plan.tagline,
            iconCode: plan.iconCode,
            logoUrl: plan.logoUrl,
          };
        const full = buildPlanFromApi({
          specialty,
          slots: apiSlots,
          prices: apiPrices,
          indicativeItems: items,
          overlay: overlayFor(specialty),
        });
        setDetailPlan(full || plan);
      } catch (err) {
        console.warn('Indicative menu load failed', err);
        setDetailPlan(plan);
      } finally {
        setDetailLoading(false);
      }
    },
    [apiPrices, apiSlots, apiSpecialties, catalogSource],
  );

  const cards = useMemo(() => plans, [plans]);

  return (
    <>
      <section className="sp-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Specialty nutrition</span>
            <h2 className="section-title">
              GENERAL, GYM, DIABETIC &amp; <span>WEIGHT LOSS</span>
            </h2>
            <p className="section-desc">
              Choose a meal type, view the full weekly menu by day (Monday–Saturday), and compare
              non-organic vs organic pricing for Breakfast, Mid Mor Snack, Lunch, Evng Snack &amp; Dinner.
            </p>
            {catalogError ? (
              <p className="sp-muted" role="status">
                Showing cached specialty plans. {catalogError}
              </p>
            ) : null}
          </div>

          <div className="sp-cards sp-cards--four">
            {cards.map((plan) => {
              const Icon = plan.iconComponent || resolveSpecialtyIcon(plan.iconCode, plan.id);
              const isActive = selectedPlanId === plan.id;
              const fromRow = plan.fromPrice || pickFromPriceRow(plan.pricing);
              const organicOnly = isOrganicOnlySpecialtyPlan(plan) || plan.organicOnly;

              return (
                <article
                  key={plan.id}
                  className={`sp-card${isActive ? ' sp-card--active' : ''}`}
                  style={{ '--sp-accent': plan.accent }}
                >
                  <div className="sp-card__icon">
                    {plan.logoUrl ? (
                      <img src={plan.logoUrl} alt="" className="sp-card__logo" />
                    ) : (
                      <Icon />
                    )}
                  </div>
                  <h3>{plan.name}</h3>
                  <p>{plan.caption || plan.tagline}</p>
                  <div className="sp-card__slots">
                    {(plan.slots?.length ? plan.slots : MEAL_SLOT_TYPES).map((t) => {
                      const slotId = t.code || t.id;
                      const meta = MEAL_SLOT_TYPES.find((m) => m.id === slotId) || t;
                      const SlotIcon = meta.icon;
                      return (
                        <span
                          key={slotId}
                          className={`sp-slot-type ${meta.className || ''} sp-slot-type--compact`}
                        >
                          {SlotIcon ? <SlotIcon className="sp-slot-type__icon" aria-hidden /> : null}
                          <span>{meta.label || t.label}</span>
                        </span>
                      );
                    })}
                  </div>
                  {fromRow ? (
                    <p className="sp-card__from">
                      {!organicOnly && fromRow.perDayNonOrganic != null ? (
                        <span className="sp-card__price-row">
                          <span className="sp-card__price-tag sp-card__price-tag--std">Regular</span>
                          <strong>₹{fromRow.perDayNonOrganic}</strong>/day
                        </span>
                      ) : null}
                      {fromRow.perDayOrganic != null ? (
                        <span className="sp-card__price-row sp-card__organic-hint">
                          <span className="sp-card__price-tag sp-card__price-tag--org">Organic</span>
                          {organicOnly ? (
                            <>
                              <strong>₹{fromRow.perDayOrganic}</strong>/day
                            </>
                          ) : (
                            <>from ₹{fromRow.perDayOrganic}/day</>
                          )}
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                  <div className="sp-card__actions">
                    <button
                      type="button"
                      className="sp-card__btn sp-card__btn--ghost"
                      onClick={() => openDetails(plan)}
                      disabled={detailLoading && detailPlan?.id === plan.id}
                    >
                      <FaEye /> {detailLoading && detailPlan?.id === plan.id ? 'Loading…' : 'View details'}
                    </button>
                    {!hideSelectButton ? (
                      <button
                        type="button"
                        className="sp-card__btn sp-card__btn--primary"
                        onClick={() => setSelectPlan(plan)}
                      >
                        Select <FaArrowRight />
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {detailPlan ? (
        <SpecialtyPlanDetailModal
          plan={detailPlan}
          loadingMenu={detailLoading || detailPlan._loadingMenu}
          onClose={() => setDetailPlan(null)}
        />
      ) : null}

      {selectPlan && !hideSelectButton ? (
        <SelectPlanModal
          plan={selectPlan}
          onClose={() => setSelectPlan(null)}
          onConfirm={({ organic, regionKey, slotIds }) => {
            onSelectPlan?.(selectPlan, { organic, regionKey, slotIds });
            setSelectPlan(null);
          }}
        />
      ) : null}
    </>
  );
}
